import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Building, ArrowRight, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DualWorkflowSelector } from './DualWorkflowSelector';
import { SmartAvailabilityCalendar } from './SmartAvailabilityCalendar';
import { EnhancedUnitGrid } from './EnhancedUnitGrid';
import { DateRangePicker } from './DateRangePicker';
import { useUnitAvailability } from '../../hooks/useUnitAvailability';

export const FlexibleBookingSelector = ({
    units,
    unitBookings = [],
    selectedUnit,
    selectedCheckIn,
    selectedCheckOut,
    onUnitChange,
    onDateChange,
    excludeBookingId,
    isLoading
}) => {
    const { t } = useTranslation();

    const [workflow, setWorkflow] = useState('dates-first');
    const [showValidation, setShowValidation] = useState(false);

    // Auto-detect workflow based on what user selects first
    useEffect(() => {
        if (selectedUnit && !selectedCheckIn && !selectedCheckOut) {
            setWorkflow('unit-first');
        } else if ((selectedCheckIn || selectedCheckOut) && !selectedUnit) {
            setWorkflow('dates-first');
        }
    }, [selectedUnit, selectedCheckIn, selectedCheckOut]);

    const hasSelection = selectedUnit || selectedCheckIn || selectedCheckOut;
    const isSelectionComplete = selectedUnit && selectedCheckIn && selectedCheckOut;
    const selectedUnitData = units.find(unit => unit.id === selectedUnit);

    const handleWorkflowChange = (newWorkflow) => {
        setWorkflow(newWorkflow);
        // Clear selections when switching workflows
        onUnitChange('');
        onDateChange('', '');
    };

    const handleUnitSelect = (unitId) => {
        onUnitChange(unitId);
        setShowValidation(true);
    };

    const handleDateSelect = (checkIn, checkOut) => {
        onDateChange(checkIn, checkOut);
        setShowValidation(true);
    };

    return (
        <div className="pt-4 pb-3 px-2 mx-3">
            {/* Workflow Selector */}
            {/* <DualWorkflowSelector
                selectedWorkflow={workflow}
                onWorkflowChange={handleWorkflowChange}
                hasSelection={hasSelection}
            /> */}

            {/* Selection Status */}
            {/* {isSelectionComplete && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle size={24} className="text-green-600" />
                        <div>
                            <h4 className="font-semibold text-green-900 text-sm sm:text-base">
                                {t('selectionComplete')}
                            </h4>
                            <p className="text-green-700 text-xs sm:text-sm">
                                {selectedUnitData?.type} {selectedUnitData?.number} - {moment(selectedCheckIn).format('L')} {t('to')} {moment(selectedCheckOut).format('L')}
                            </p>
                        </div>
                    </div>
                </div>
            )} */}

            {/* Workflow Content */}
            {workflow === 'dates-first' ? (
                <div className="space-y-4 sm:space-y-6">
                    {/* Step 1: Date Selection */}
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                                1
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                                {t('selectYourDates')}
                            </h3>
                        </div>

                        <DateRangePicker
                            unitId=""
                            selectedCheckIn={selectedCheckIn}
                            selectedCheckOut={selectedCheckOut}
                            onDateChange={handleDateSelect}
                            excludeBookingId={excludeBookingId}
                        />
                    </div>

                    {/* Step 2: Unit Selection (only show when dates are selected) */}
                    {selectedCheckIn && selectedCheckOut && (
                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                                    2
                                </div>
                                <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                                    {t('selectAvailableUnit')}
                                </h3>
                            </div>

                            <EnhancedUnitGrid
                                units={units}
                                selectedUnit={selectedUnit}
                                checkIn={selectedCheckIn}
                                checkOut={selectedCheckOut}
                                onUnitSelect={handleUnitSelect}
                                excludeBookingId={excludeBookingId}
                                bookings={unitBookings}
                                isLoading={isLoading}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-4 sm:space-y-6">
                    {/* Step 1: Unit Selection */}
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                                1
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                                {t('selectYourUnit')}
                            </h3>
                        </div>

                        <EnhancedUnitGrid
                            units={units}
                            selectedUnit={selectedUnit}
                            checkIn=""
                            checkOut=""
                            onUnitSelect={handleUnitSelect}
                            excludeBookingId={excludeBookingId}
                            bookings={unitBookings}
                        />
                    </div>

                    {/* Step 2: Date Selection (only show when unit is selected) */}
                    {selectedUnit && selectedUnitData && (
                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                                    2
                                </div>
                                <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                                    {t('selectAvailableDates')}
                                </h3>
                            </div>

                            <SmartAvailabilityCalendar
                                unitId={selectedUnit}
                                unitNumber={selectedUnitData.number}
                                selectedCheckIn={selectedCheckIn}
                                selectedCheckOut={selectedCheckOut}
                                onDateChange={handleDateSelect}
                                excludeBookingId={excludeBookingId}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Workflow Guide */}
            {/* {!hasSelection && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200">
                    <div className="text-center">
                        <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">
                            {t('getStarted')}
                        </h3>
                        <p className="text-slate-600 mb-4 text-sm sm:text-base px-2">
                            {workflow === 'dates-first'
                                ? t('datesFirstGuide')
                                : t('unitFirstGuide')
                            }
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 rtl:sm:space-x-reverse">
                            <div className={`flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 rounded-lg ${workflow === 'dates-first' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                                }`}>
                                <Calendar size={16} />
                                <span className="text-xs sm:text-sm font-medium">
                                    {workflow === 'dates-first' ? t('step1Dates') : t('step2Dates')}
                                </span>
                            </div>
                            <ArrowRight size={16} className="text-slate-400 hidden sm:block" />
                            <div className="w-px h-4 bg-slate-300 sm:hidden"></div>
                            <div className={`flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 rounded-lg ${workflow === 'unit-first' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                                }`}>
                                <Building size={16} />
                                <span className="text-xs sm:text-sm font-medium">
                                    {workflow === 'unit-first' ? t('step1Unit') : t('step2Unit')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )} */}
        </div>
    );
};
